import React from "react";
import WebProfitSpinner from "../../components/system/WebProfitSpinner";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ResponsiveOperationalTable from "../../components/dashboard/ResponsiveOperationalTable";
import DashboardLoadingState from "../../components/dashboard/DashboardLoadingState";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import PriceChangeRoundedIcon from "@mui/icons-material/PriceChangeRounded";
import PendingActionsRoundedIcon from "@mui/icons-material/PendingActionsRounded";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import CardGiftcardRoundedIcon from "@mui/icons-material/CardGiftcardRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import ViewModuleRoundedIcon from "@mui/icons-material/ViewModuleRounded";
import { planBillingApi } from "../../api/planBilling";

const BRAND = {
  orange: "#F35A3A",
  dark: "var(--wp-panel-text-primary, #19242F)",
  page: "var(--wp-panel-bg, #F6F7FB)",
  border: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.10)",
  muted: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.62)",
};

function money(value, currency = "ARS") {
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency,
    maximumFractionDigits: currency === "ARS" ? 0 : 2,
  }).format(Number(value || 0));
}

function dateLabel(value) {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return new Intl.DateTimeFormat("es-AR", { dateStyle: "medium" }).format(parsed);
}

function recurringRevenueLabel(metrics = {}) {
  const entries = Object.entries(metrics.monthlyRecurringByCurrency || {}).filter(
    ([, amount]) => Number(amount || 0) > 0,
  );
  if (!entries.length) {
    return money(metrics.monthlyRecurring || 0, metrics.revenueCurrency || "ARS");
  }
  return entries.map(([currency, amount]) => money(amount, currency)).join(" + ");
}

function statusMeta(status) {
  const map = {
    trial: { label: "Prueba", color: "warning" },
    pending: { label: "Pendiente", color: "warning" },
    active: { label: "Activa", color: "success" },
    past_due: { label: "Pago atrasado", color: "error" },
    paused: { label: "Pausada", color: "error" },
    cancelled: { label: "Cancelada", color: "default" },
    exempt: { label: "Bonificada", color: "success" },
    authorized: { label: "Autorizada", color: "success" },
    rejected: { label: "Rechazada", color: "error" },
  };
  return map[status] || { label: status || "Sin estado", color: "default" };
}

function Metric({ icon, label, value, helper }) {
  return (
    <Card elevation={0} sx={{ height: "100%", borderRadius: 4, border: `1px solid ${BRAND.border}`, boxShadow: "0 14px 34px rgba(var(--wp-panel-shadow-rgb, 25,36,47),.05)" }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2.5 }, "&:last-child": { pb: { xs: 1.5, sm: 2.5 } } }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          <Avatar variant="rounded" sx={{ width: 44, height: 44, borderRadius: 2.5, bgcolor: "rgba(243,90,58,.10)", color: BRAND.orange }}>
            {icon}
          </Avatar>
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 900, color: BRAND.muted }}>{label}</Typography>
            <Typography variant="h5" sx={{ mt: 0.25, fontWeight: 950, color: BRAND.dark, fontSize: { xs: "1.25rem", sm: "1.5rem" }, overflowWrap: "anywhere" }}>{value}</Typography>
            <Typography variant="body2" sx={{ display: { xs: "none", sm: "block" }, mt: 0.6, color: BRAND.muted }}>{helper}</Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

function ServicePriceEditor({ service, saving, onSave }) {
  const [price, setPrice] = React.useState(String(Number(service?.monthlyPrice || 0)));

  React.useEffect(() => {
    setPrice(String(Number(service?.monthlyPrice || 0)));
  }, [service?.monthlyPrice]);

  const parsed = Number(price);
  const invalid = !Number.isFinite(parsed) || parsed < 0;
  const dirty = !invalid && Number(parsed.toFixed(2)) !== Number(Number(service?.monthlyPrice || 0).toFixed(2));

  return (
    <Paper elevation={0} sx={{ p: 2.25, borderRadius: 3.5, border: `1px solid ${BRAND.border}`, height: "100%" }}>
      <Stack spacing={1.4} sx={{ height: "100%" }}>
        <Box>
          <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" sx={{ color: BRAND.orange, fontWeight: 950, textTransform: "uppercase" }}>{service.code}</Typography>
              <Typography sx={{ mt: .25, fontWeight: 950, color: BRAND.dark }}>{service.name}</Typography>
            </Box>
            <Chip size="small" label={service.currency || "ARS"} variant="outlined" />
          </Stack>
          <Typography variant="body2" sx={{ mt: .6, color: BRAND.muted, minHeight: 40 }}>{service.description || "Precio mensual general."}</Typography>
        </Box>
        <TextField
          label="Precio mensual general"
          type="number"
          value={price}
          error={invalid}
          helperText={invalid ? "Ingresá cero o un valor mayor." : "Este valor se usa salvo que el cliente tenga un precio particular."}
          onChange={(event) => setPrice(event.target.value)}
          inputProps={{ min: 0, step: 1 }}
          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
        />
        <Button
          disabled={saving || invalid || !dirty}
          onClick={() => onSave(service.code, { monthlyPrice: parsed })}
          variant="contained"
          startIcon={saving ? <WebProfitSpinner size={16} color="inherit" /> : <SaveRoundedIcon />}
          sx={{ mt: "auto", borderRadius: 999, bgcolor: BRAND.orange, textTransform: "none", fontWeight: 950, "&:hover": { bgcolor: "#DE4D30" } }}
        >
          Guardar precio
        </Button>
      </Stack>
    </Paper>
  );
}

function applyLocalModuleDependencies(modules, key, enabled) {
  const next = { ...modules, [key]: Boolean(enabled), landing: true };
  if (next.orders) next.products = true;
  if (!next.products) next.orders = false;
  if (next.memberships) next.customers = true;
  if (!next.customers) next.memberships = false;
  if (next.table_reservations) next.tables = true;
  if (!next.tables) {
    next.table_reservations = false;
    next.kitchen_orders = false;
  }
  if (next.kitchen_orders) {
    next.tables = true;
    next.orders = true;
    next.products = true;
  }
  if (next.recipes) next.products = true;
  if (next.leases) next.properties = true;
  if (next.lease_collections) {
    next.leases = true;
    next.properties = true;
  }
  if (!next.properties) {
    next.leases = false;
    next.lease_collections = false;
  }
  if (!next.leases) next.lease_collections = false;
  return next;
}

function ProjectModulesDialog({ open, project, catalog, saving, onClose, onSave }) {
  const [modules, setModules] = React.useState({});

  React.useEffect(() => {
    if (!open || !project) return;
    const configured = project.moduleControl?.configuredModules || project.modules || {};
    setModules(Object.fromEntries((catalog || []).map((item) => [item.key, item.key === "landing" ? true : configured?.[item.key] === true])));
  }, [open, project, catalog]);

  if (!project) return null;

  const groups = (catalog || []).reduce((result, item) => {
    const group = item.group || "Otros";
    if (!result[group]) result[group] = [];
    result[group].push(item);
    return result;
  }, {});

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: { xs: 0, sm: 3 }, m: { xs: 0, sm: 2 }, maxHeight: { xs: "100dvh", sm: "calc(100% - 48px)" } } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 950, color: BRAND.dark }}>Módulos de {project.name}</Typography>
        <Typography variant="body2" sx={{ mt: .35, color: BRAND.muted }}>Control técnico manual del Owner. No cambia precios, mensualidad ni servicios cobrados.</Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.2}>
          <Alert severity="info" sx={{ borderRadius: 3 }}>
            Las dependencias se conservan automáticamente. Ejemplo: Pedidos necesita Productos y Membresías necesita CRM.
          </Alert>
          {Object.entries(groups).map(([group, items]) => (
            <Box key={group}>
              <Typography variant="overline" sx={{ fontWeight: 950, color: BRAND.muted }}>{group}</Typography>
              <Grid container spacing={1.2} sx={{ mt: .1 }}>
                {items.map((item) => (
                  <Grid item xs={12} md={6} key={item.key}>
                    <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2.5, border: `1px solid ${BRAND.border}`, bgcolor: modules[item.key] ? "rgba(46,125,50,.035)" : "transparent" }}>
                      <Stack direction="row" justifyContent="space-between" spacing={1.2} alignItems="flex-start">
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>{item.label}</Typography>
                          <Typography variant="caption" sx={{ display: "block", mt: .25, color: BRAND.muted }}>{item.description}</Typography>
                        </Box>
                        <Switch
                          checked={item.key === "landing" ? true : modules[item.key] === true}
                          disabled={Boolean(item.locked)}
                          onChange={(event) => setModules((current) => applyLocalModuleDependencies(current, item.key, event.target.checked))}
                        />
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button disabled={saving} onClick={onClose} sx={{ textTransform: "none", fontWeight: 900 }}>Cancelar</Button>
        <Button
          disabled={saving}
          onClick={() => onSave(project.id || project._id || project.slug, { modules })}
          variant="contained"
          startIcon={saving ? <WebProfitSpinner size={16} color="inherit" /> : <SaveRoundedIcon />}
          sx={{ bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 950, "&:hover": { bgcolor: "#DE4D30" } }}
        >
          Guardar módulos
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ProjectPricingDialog({ open, project, services, saving, onClose, onSave }) {
  const [overrides, setOverrides] = React.useState({});
  const [applyToActiveSubscription, setApplyToActiveSubscription] = React.useState(false);

  React.useEffect(() => {
    const current = project?.billingSummary?.servicePriceOverrides || {};
    const next = {};
    for (const service of services || []) {
      next[service.code] = Object.prototype.hasOwnProperty.call(current, service.code)
        ? String(current[service.code])
        : "";
    }
    setOverrides(next);
    setApplyToActiveSubscription(false);
  }, [project, services, open]);

  if (!project) return null;

  const summary = project.billingSummary || {};
  const selectedCodes = new Set(
    summary.pendingServiceCodes?.length
      ? summary.pendingServiceCodes
      : summary.serviceCodes || [],
  );
  const hasActiveRecurringSubscription = Boolean(
    summary.subscriptionId &&
      summary.status === "active" &&
      summary.subscriptionStatus === "authorized",
  );

  const parsedPrice = (service) => {
    const raw = String(overrides[service.code] ?? "").trim();
    if (raw === "") return Number(service.monthlyPrice || 0);
    const amount = Number(raw);
    return Number.isFinite(amount) && amount >= 0 ? amount : null;
  };

  const invalidCodes = (services || [])
    .filter((service) => parsedPrice(service) === null)
    .map((service) => service.code);
  const hasInvalidPrice = invalidCodes.length > 0;
  const customCount = Object.values(overrides).filter(
    (value) => String(value).trim() !== "",
  ).length;
  const currentTotal = Number(summary.monthlyAmount || 0);
  const estimatedTotal = (services || [])
    .filter((service) => selectedCodes.has(service.code))
    .reduce((total, service) => total + Number(parsedPrice(service) || 0), 0);

  const save = () => {
    if (hasInvalidPrice) return;
    const cleanOverrides = {};
    for (const service of services || []) {
      const raw = String(overrides[service.code] ?? "").trim();
      if (raw !== "") cleanOverrides[service.code] = Number(raw);
    }
    onSave(project.id || project._id || project.slug, {
      overrides: cleanOverrides,
      applyToActiveSubscription,
    });
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md" PaperProps={{ sx: { m: { xs: 0, sm: 2 }, width: { xs: "100%", sm: "calc(100% - 32px)" }, maxHeight: { xs: "100dvh", sm: "calc(100% - 64px)" }, borderRadius: { xs: 0, sm: 2 } } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>
          Precios para {project.name}
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5, color: BRAND.muted }}>
          Cada valor se aplica solamente a este proyecto. Vacío usa el precio general; cero bonifica esa capacidad.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              bgcolor: BRAND.page,
              border: `1px solid ${BRAND.border}`,
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              spacing={2}
            >
              <Box>
                <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 900 }}>
                  MENSUALIDAD ACTUAL
                </Typography>
                <Typography variant="h6" sx={{ mt: 0.3, color: BRAND.dark, fontWeight: 950 }}>
                  {money(currentTotal, summary.currency || "ARS")}
                </Typography>
              </Box>
              <Box sx={{ textAlign: { sm: "right" } }}>
                <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 900 }}>
                  TOTAL ESTIMADO CON ESTOS PRECIOS
                </Typography>
                <Typography variant="h6" sx={{ mt: 0.3, color: BRAND.orange, fontWeight: 950 }}>
                  {money(estimatedTotal, summary.currency || "ARS")}
                </Typography>
              </Box>
            </Stack>
            <Typography variant="caption" sx={{ display: "block", mt: 1.2, color: BRAND.muted }}>
              El total estimado usa las capacidades que el cliente tiene contratadas actualmente.
            </Typography>
          </Paper>

          <Stack spacing={1.25}>
            {(services || []).map((service) => {
              const raw = overrides[service.code] ?? "";
              const hasOverride = String(raw).trim() !== "";
              const isSelected = selectedCodes.has(service.code);
              const invalid = invalidCodes.includes(service.code);
              return (
                <Paper
                  key={service.code}
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    borderColor: invalid
                      ? "error.main"
                      : hasOverride
                        ? "rgba(243,90,58,.42)"
                        : BRAND.border,
                  }}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ sm: "center" }}
                    spacing={1.5}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>{service.name}</Typography>
                        {isSelected ? <Chip size="small" label="Contratado" color="success" /> : null}
                        {hasOverride ? <Chip size="small" label="Precio particular" color="warning" /> : null}
                      </Stack>
                      <Typography variant="body2" sx={{ mt: 0.45, color: BRAND.muted }}>
                        General: {money(service.monthlyPrice, service.currency)} / mes
                      </Typography>
                    </Box>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={0.75}
                      alignItems={{ sm: "center" }}
                      sx={{ width: { xs: "100%", sm: "auto" } }}
                    >
                      <TextField
                        label="Precio para este cliente"
                        type="number"
                        size="small"
                        value={raw}
                        error={invalid}
                        helperText={invalid ? "Ingresá cero o un importe positivo" : " "}
                        onChange={(event) =>
                          setOverrides((current) => ({ ...current, [service.code]: event.target.value }))
                        }
                        inputProps={{ min: 0, step: 1 }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        }}
                        sx={{ width: { xs: "100%", sm: 230 } }}
                      />
                      {hasOverride ? (
                        <Button
                          size="small"
                          onClick={() =>
                            setOverrides((current) => ({ ...current, [service.code]: "" }))
                          }
                          sx={{
                            minWidth: { sm: 118 },
                            textTransform: "none",
                            fontWeight: 900,
                            whiteSpace: "nowrap",
                          }}
                        >
                          Usar general
                        </Button>
                      ) : null}
                    </Stack>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>

          {hasActiveRecurringSubscription ? (
            <Alert severity="warning" sx={{ borderRadius: 3 }}>
              <FormControlLabel
                sx={{ m: 0, alignItems: "flex-start" }}
                control={
                  <Switch
                    checked={applyToActiveSubscription}
                    onChange={(event) => setApplyToActiveSubscription(event.target.checked)}
                  />
                }
                label={
                  <Box sx={{ pt: 0.6 }}>
                    <Typography sx={{ fontWeight: 900 }}>Actualizar ahora la mensualidad en Mercado Pago</Typography>
                    <Typography variant="body2">
                      Cambia el importe recurrente de las capacidades ya contratadas. Si lo dejás apagado, el precio queda guardado para el próximo cambio de plan.
                    </Typography>
                  </Box>
                }
              />
            </Alert>
          ) : (
            <Alert severity="info" sx={{ borderRadius: 3 }}>
              Los precios quedan guardados para este proyecto y se usarán cuando el cliente autorice o actualice su plan.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2, flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "stretch", sm: "center" }, gap: { xs: .75, sm: 0 }, "& .MuiButton-root": { ml: { xs: "0 !important", sm: undefined }, minHeight: 42 } }}>
        <Typography variant="caption" sx={{ mr: { sm: "auto" }, color: BRAND.muted, fontWeight: 800 }}>
          {customCount} capacidad(es) con precio particular
        </Typography>
        <Button disabled={saving} onClick={onClose} sx={{ textTransform: "none", fontWeight: 900 }}>
          Cancelar
        </Button>
        <Button
          disabled={saving || hasInvalidPrice}
          onClick={save}
          variant="contained"
          startIcon={saving ? <WebProfitSpinner size={16} color="inherit" /> : <SaveRoundedIcon />}
          sx={{ bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 950, "&:hover": { bgcolor: "#DE4D30" } }}
        >
          Guardar precios
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AdminSettlementDialog({ open, project, action, saving, onClose, onConfirm }) {
  const [periodMonths, setPeriodMonths] = React.useState(1);
  const [amount, setAmount] = React.useState("");
  const [notes, setNotes] = React.useState("");

  const summary = project?.billingSummary || {};
  const isExempt = action === "exempt";
  const monthlyAmount = Number(summary.monthlyAmount || 0);
  const expectedAmount = monthlyAmount * periodMonths;
  const hasProviderAuthorization = Boolean(
    summary.subscriptionId || summary.pendingSubscriptionId,
  );

  React.useEffect(() => {
    if (!open) return;
    setPeriodMonths(1);
    setAmount(String(Number(summary.monthlyAmount || 0)));
    setNotes("");
  }, [open, project, action, summary.monthlyAmount]);

  if (!project) return null;

  const submit = () => {
    if (isExempt) {
      onConfirm(project.id || project._id || project.slug, {
        action: "exempt",
        notes,
      });
      return;
    }

    const parsedAmount = Number(amount);
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) return;
    onConfirm(project.id || project._id || project.slug, {
      action: "manual_payment",
      periodMonths,
      amount: parsedAmount,
      notes,
    });
  };

  const invalidAmount = !isExempt && (!Number.isFinite(Number(amount)) || Number(amount) <= 0);

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { m: { xs: 0, sm: 2 }, width: { xs: "100%", sm: "calc(100% - 32px)" }, maxHeight: { xs: "100dvh", sm: "calc(100% - 64px)" }, borderRadius: { xs: 0, sm: 2 } } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.2} alignItems="center">
          <Avatar
            variant="rounded"
            sx={{
              bgcolor: isExempt ? "rgba(46,125,50,.10)" : "rgba(243,90,58,.10)",
              color: isExempt ? "success.main" : BRAND.orange,
            }}
          >
            {isExempt ? <CardGiftcardRoundedIcon /> : <PaidRoundedIcon />}
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 950, color: BRAND.dark }}>
              {isExempt ? "Bonificar capacidad" : "Registrar pago manual"}
            </Typography>
            <Typography variant="body2" sx={{ color: BRAND.muted }}>
              {project.name}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.2}>
          {hasProviderAuthorization ? (
            <Alert severity="warning" sx={{ borderRadius: 3 }}>
              Para evitar un cobro doble, esta acción cancelará la autorización recurrente o pendiente de Mercado Pago antes de aplicar el cambio.
            </Alert>
          ) : null}

          {isExempt ? (
            <Alert severity="success" sx={{ borderRadius: 3 }}>
              El proyecto quedará activo sin cargo y sin fecha de vencimiento. El valor mensual configurado se conserva para que puedas volver a cobrarlo más adelante.
            </Alert>
          ) : (
            <>
              <TextField
                select
                label="Período cubierto"
                value={periodMonths}
                onChange={(event) => {
                  const nextMonths = Number(event.target.value);
                  setPeriodMonths(nextMonths);
                  setAmount(String(monthlyAmount * nextMonths));
                }}
              >
                <MenuItem value={1}>1 mes</MenuItem>
                <MenuItem value={3}>3 meses</MenuItem>
                <MenuItem value={6}>6 meses</MenuItem>
                <MenuItem value={12}>12 meses</MenuItem>
              </TextField>
              <TextField
                label="Monto recibido"
                type="number"
                value={amount}
                error={invalidAmount}
                helperText={
                  invalidAmount
                    ? "Ingresá un importe mayor a cero"
                    : `Importe esperado según las capacidades: ${money(expectedAmount, summary.currency || "ARS")}`
                }
                onChange={(event) => setAmount(event.target.value)}
                inputProps={{ min: 0.01, step: 1 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
              <Paper
                elevation={0}
                sx={{ p: 2, borderRadius: 3, bgcolor: BRAND.page, border: `1px solid ${BRAND.border}` }}
              >
                <Typography variant="caption" sx={{ color: BRAND.muted, fontWeight: 900 }}>
                  NUEVO VENCIMIENTO
                </Typography>
                <Typography sx={{ mt: 0.4, fontWeight: 950, color: BRAND.dark }}>
                  Se calculará desde hoy o desde el final del período manual que todavía esté vigente.
                </Typography>
              </Paper>
            </>
          )}

          <TextField
            label="Nota administrativa"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            multiline
            minRows={2}
            inputProps={{ maxLength: 1000 }}
            placeholder={isExempt ? "Ej.: bonificación comercial" : "Ej.: transferencia recibida"}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2, flexDirection: { xs: "column-reverse", sm: "row" }, alignItems: { xs: "stretch", sm: "center" }, gap: { xs: .75, sm: 0 }, "& .MuiButton-root": { ml: { xs: "0 !important", sm: undefined }, minHeight: 42 } }}>
        <Button disabled={saving} onClick={onClose} sx={{ textTransform: "none", fontWeight: 900 }}>
          Cancelar
        </Button>
        <Button
          disabled={saving || invalidAmount}
          onClick={submit}
          variant="contained"
          startIcon={
            saving ? (
              <WebProfitSpinner size={16} color="inherit" />
            ) : isExempt ? (
              <CardGiftcardRoundedIcon />
            ) : (
              <PaidRoundedIcon />
            )
          }
          sx={{
            bgcolor: isExempt ? "success.main" : BRAND.orange,
            borderRadius: 999,
            textTransform: "none",
            fontWeight: 950,
            "&:hover": { bgcolor: isExempt ? "success.dark" : "#DE4D30" },
          }}
        >
          {isExempt ? "Confirmar bonificación" : "Registrar pago"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function BillingPlansAdminPage() {
  const [data, setData] = React.useState(null);
  const [tab, setTab] = React.useState(0);
  const [query, setQuery] = React.useState("");
  const [moduleQuery, setModuleQuery] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [pricingProject, setPricingProject] = React.useState(null);
  const [settlement, setSettlement] = React.useState(null);
  const [modulesProject, setModulesProject] = React.useState(null);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      setData(await planBillingApi.adminOverview());
    } catch (err) {
      setError(err.message || "No se pudo cargar el control comercial del Owner");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const updateService = async (code, payload) => {
    try {
      setSaving(true);
      setError("");
      setMessage("");
      const response = await planBillingApi.updateService(code, payload);
      setMessage(response?.message || `Precio de ${code} actualizado.`);
      await load();
      return true;
    } catch (err) {
      setError(err.message || "No se pudo guardar el precio general");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const updateProjectPricing = async (projectId, payload) => {
    try {
      setSaving(true);
      setError("");
      setMessage("");
      const response = await planBillingApi.updateProjectPricing(projectId, payload);
      setMessage(response?.message || "Precios particulares actualizados.");
      setPricingProject(null);
      await load();
      return true;
    } catch (err) {
      setError(err.message || "No se pudieron guardar los precios particulares");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const settleProject = async (projectId, payload) => {
    try {
      setSaving(true);
      setError("");
      setMessage("");
      const response = await planBillingApi.settleProject(projectId, payload);
      setMessage(response?.message || "Estado comercial actualizado.");
      setSettlement(null);
      await load();
      return true;
    } catch (err) {
      setError(err.message || "No se pudo registrar la acción comercial");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const updateProjectModules = async (projectId, payload) => {
    try {
      setSaving(true);
      setError("");
      setMessage("");
      const response = await planBillingApi.updateProjectModules(projectId, payload);
      setMessage(response?.message || "Módulos actualizados.");
      setModulesProject(null);
      await load();
      return true;
    } catch (err) {
      setError(err.message || "No se pudieron actualizar los módulos");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const normalizedQuery = query.trim().toLowerCase();
  const projects = (data?.projects || []).filter((project) => {
    if (!normalizedQuery) return true;
    const summary = project.billingSummary || {};
    return [
      project.name,
      project.slug,
      project.owner?.email,
      summary.status,
      ...(summary.selectedServices || []).map((service) => service.name),
    ]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(normalizedQuery));
  });

  const normalizedModuleQuery = moduleQuery.trim().toLowerCase();
  const moduleProjects = (data?.projects || []).filter((project) => {
    if (!normalizedModuleQuery) return true;
    return [project.name, project.slug, project.owner?.email]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(normalizedModuleQuery));
  });

  const pending = (data?.subscriptions || []).filter((subscription) => subscription.status === "pending");

  if (loading && !data) {
    return <DashboardLoadingState label="Cargando precios y control de proyectos…" minHeight={440} />;
  }

  return (
    <Stack spacing={{ xs: 2, sm: 2.5 }} sx={{ pb: 4, minWidth: 0 }}>
      <Paper elevation={0} sx={{ position: "relative", overflow: "hidden", p: { xs: 2, sm: 2.5, md: 3.5 }, borderRadius: 5, color: "#fff", background: "linear-gradient(135deg,#19242F 0%,#2A4052 68%,#3B596D 100%)", boxShadow: "0 22px 55px rgba(var(--wp-panel-shadow-rgb, 25,36,47),.20)" }}>
        <Box sx={{ position: "absolute", width: 280, height: 280, borderRadius: "50%", right: -90, top: -150, bgcolor: "rgba(243,90,58,.28)" }} />
        <Stack position="relative" direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "center" }} spacing={2.5}>
          <Box>
            <Typography variant="overline" sx={{ fontWeight: 900, letterSpacing: ".14em", color: "rgba(255,255,255,.65)" }}>OWNER · CONTROL COMERCIAL</Typography>
            <Typography variant="h3" sx={{ mt: .3, fontWeight: 950, letterSpacing: "-.04em", fontSize: { xs: "1.75rem", sm: "2rem", md: "2.55rem" } }}>Precios y control de proyectos</Typography>
            <Typography sx={{ mt: .65, maxWidth: 760, color: "rgba(255,255,255,.72)", fontSize: { xs: ".9rem", sm: "1rem" } }}>
              Precios generales, acuerdos particulares y habilitación manual de módulos en un solo lugar privado del Owner.
            </Typography>
          </Box>
          <Button disabled={loading || saving} onClick={load} startIcon={loading ? <WebProfitSpinner size={16} color="inherit" /> : <RefreshRoundedIcon />} variant="outlined" sx={{ color: "#fff", borderColor: "rgba(255,255,255,.38)", borderRadius: 999, textTransform: "none", fontWeight: 900, "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,.08)" } }}>
            Actualizar
          </Button>
        </Stack>
      </Paper>

      {error ? <Alert severity="error" onClose={() => setError("")} sx={{ borderRadius: 3 }}>{error}</Alert> : null}
      {message ? <Alert severity="success" onClose={() => setMessage("")} sx={{ borderRadius: 3 }}>{message}</Alert> : null}

      <Grid container spacing={1.5}>
        <Grid item xs={6} lg={3}><Metric icon={<StorefrontRoundedIcon />} label="PROYECTOS" value={data?.metrics?.projects || 0} helper="Todos los proyectos registrados." /></Grid>
        <Grid item xs={6} lg={3}><Metric icon={<CreditCardRoundedIcon />} label="VALOR MENSUAL" value={recurringRevenueLabel(data?.metrics)} helper="Valor recurrente activo observable." /></Grid>
        <Grid item xs={6} lg={3}><Metric icon={<PriceChangeRoundedIcon />} label="PRECIOS PARTICULARES" value={(data?.projects || []).filter((project) => Object.keys(project.billing?.servicePriceOverrides || {}).length > 0).length} helper="Clientes con al menos una excepción." /></Grid>
        <Grid item xs={6} lg={3}><Metric icon={<PendingActionsRoundedIcon />} label="PENDIENTES" value={data?.metrics?.pending || 0} helper={`${pending.length} autorización(es) de cobro pendiente(s).`} /></Grid>
      </Grid>

      <Paper elevation={0} sx={{ borderRadius: 3.5, border: `1px solid ${BRAND.border}`, overflow: "hidden" }}>
        <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable" scrollButtons="auto" sx={{ px: { xs: 1, sm: 1.5 }, borderBottom: `1px solid ${BRAND.border}`, "& .MuiTab-root": { minHeight: 58, textTransform: "none", fontWeight: 900 } }}>
          <Tab icon={<PriceChangeRoundedIcon fontSize="small" />} iconPosition="start" label="Precios generales" />
          <Tab icon={<StorefrontRoundedIcon fontSize="small" />} iconPosition="start" label="Clientes / acuerdos" />
          <Tab icon={<ViewModuleRoundedIcon fontSize="small" />} iconPosition="start" label="Módulos" />
        </Tabs>

        <Box sx={{ p: { xs: 1.5, sm: 2.5, md: 3 } }}>
          {tab === 0 ? (
            <Stack spacing={2.25}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>Precios generales</Typography>
                <Typography variant="body2" sx={{ mt: .45, color: BRAND.muted }}>Acá sólo se edita el precio mensual base. No se cambian módulos, reglas internas ni la estructura de los servicios.</Typography>
              </Box>
              <Grid container spacing={1.5}>
                {(data?.services || []).map((service) => (
                  <Grid item xs={12} md={6} xl={4} key={service.code}>
                    <ServicePriceEditor service={service} saving={saving} onSave={updateService} />
                  </Grid>
                ))}
              </Grid>
            </Stack>
          ) : null}

          {tab === 1 ? (
            <Stack spacing={2}>
              {data && data.mercadoPagoConfigured === false ? (
                <Alert severity="warning" sx={{ borderRadius: 3 }}>
                  Mercado Pago no está configurado en backend. Igual podés personalizar precios, registrar pagos manuales o bonificar.
                </Alert>
              ) : null}
              <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "center" }} spacing={1.5}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>Clientes y acuerdos particulares</Typography>
                  <Typography variant="body2" sx={{ mt: .45, color: BRAND.muted }}>Personalizá precios, registrá un pago recibido o bonificá un proyecto.</Typography>
                </Box>
                <TextField size="small" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar cliente o proyecto" InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon /></InputAdornment> }} sx={{ width: { xs: "100%", md: 330 } }} />
              </Stack>

              <TableContainer sx={{ overflow: "visible", borderRadius: 3, border: { xs: 0, md: `1px solid ${BRAND.border}` } }}>
                <ResponsiveOperationalTable mobileLabel="Clientes" collapseAfter={3} sx={{ minWidth: 900 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: BRAND.page }}>
                      <TableCell>Cliente / proyecto</TableCell>
                      <TableCell>Servicios cobrados</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell align="right">Mensualidad</TableCell>
                      <TableCell align="right">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {projects.map((project) => {
                      const summary = project.billingSummary || {};
                      const meta = statusMeta(summary.status);
                      const names = (summary.selectedServices || []).map((service) => service.name);
                      return (
                        <TableRow key={project.id || project.slug} hover>
                          <TableCell>
                            <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>{project.name}</Typography>
                            <Typography variant="caption" sx={{ color: BRAND.muted }}>{project.owner?.email || `/${project.slug}`}</Typography>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 350 }}>
                            <Stack direction="row" spacing={.5} useFlexGap flexWrap="wrap">
                              {names.length ? names.map((name) => <Chip key={name} size="small" label={name} variant="outlined" />) : <Typography variant="body2" sx={{ color: BRAND.muted }}>Sin selección cobrada</Typography>}
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Stack spacing={.45} alignItems="flex-start">
                              <Chip size="small" label={meta.label} color={meta.color} />
                              <Typography variant="caption" sx={{ color: BRAND.muted }}>{summary.nextPaymentDate ? `Próximo: ${dateLabel(summary.nextPaymentDate)}` : "Sin próximo cobro"}</Typography>
                            </Stack>
                          </TableCell>
                          <TableCell align="right"><Typography sx={{ fontWeight: 950 }}>{money(summary.monthlyAmount || 0, summary.currency || "ARS")}</Typography></TableCell>
                          <TableCell align="right">
                            <Stack direction="row" justifyContent="flex-end" spacing={.3} useFlexGap flexWrap="wrap">
                              <Button disabled={saving} startIcon={<PriceChangeRoundedIcon />} onClick={() => setPricingProject(project)} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900, color: BRAND.dark }}>Precios</Button>
                              <Button disabled={saving} startIcon={<PaidRoundedIcon />} onClick={() => setSettlement({ project, action: "manual_payment" })} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900, color: "success.dark" }}>Pago</Button>
                              <Button disabled={saving} startIcon={<CardGiftcardRoundedIcon />} onClick={() => setSettlement({ project, action: "exempt" })} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900, color: "warning.dark" }}>Bonificar</Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {!projects.length ? <TableRow><TableCell colSpan={5} sx={{ py: 5, textAlign: "center", color: BRAND.muted }}>No hay resultados.</TableCell></TableRow> : null}
                  </TableBody>
                </ResponsiveOperationalTable>
              </TableContainer>
            </Stack>
          ) : null}

          {tab === 2 ? (
            <Stack spacing={2}>
              <Alert severity="info" sx={{ borderRadius: 3 }}>
                Este tablero habilita o deshabilita funciones técnicas. No toca precios, pagos, Mercado Pago ni el valor acordado con el cliente.
              </Alert>
              <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "center" }} spacing={1.5}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>Módulos por proyecto</Typography>
                  <Typography variant="body2" sx={{ mt: .45, color: BRAND.muted }}>Un mando directo para saber qué tiene activo cada proyecto y cambiarlo manualmente.</Typography>
                </Box>
                <TextField size="small" value={moduleQuery} onChange={(event) => setModuleQuery(event.target.value)} placeholder="Buscar proyecto" InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon /></InputAdornment> }} sx={{ width: { xs: "100%", md: 330 } }} />
              </Stack>

              <Grid container spacing={1.25}>
                {moduleProjects.map((project) => {
                  const configured = project.moduleControl?.configuredModules || project.modules || {};
                  const activeCount = (data?.moduleCatalog || []).filter((module) => configured?.[module.key] === true || module.key === "landing").length;
                  const overriddenCount = Object.keys(project.moduleControl?.overrides || {}).filter((key) => key !== "landing").length;
                  return (
                    <Grid item xs={12} md={6} xl={4} key={`module-${project.id || project.slug}`}>
                      <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: `1px solid ${BRAND.border}`, height: "100%" }}>
                        <Stack spacing={1.35} sx={{ height: "100%" }}>
                          <Box>
                            <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>{project.name}</Typography>
                            <Typography variant="caption" sx={{ color: BRAND.muted }}>{project.owner?.email || project.slug}</Typography>
                          </Box>
                          <Stack direction="row" spacing={.6} useFlexGap flexWrap="wrap">
                            <Chip size="small" color="success" variant="outlined" label={`${activeCount} activos`} />
                            <Chip size="small" variant="outlined" label={`${overriddenCount} bajo mando Owner`} />
                          </Stack>
                          <Stack direction="row" spacing={.45} useFlexGap flexWrap="wrap" sx={{ flex: 1, alignContent: "flex-start" }}>
                            {(data?.moduleCatalog || []).filter((module) => configured?.[module.key] === true || module.key === "landing").slice(0, 7).map((module) => <Chip key={module.key} size="small" label={module.label} />)}
                            {activeCount > 7 ? <Chip size="small" label={`+${activeCount - 7}`} /> : null}
                          </Stack>
                          <Button startIcon={<TuneRoundedIcon />} onClick={() => setModulesProject(project)} variant="outlined" sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}>
                            Gestionar módulos
                          </Button>
                        </Stack>
                      </Paper>
                    </Grid>
                  );
                })}
                {!moduleProjects.length ? <Grid item xs={12}><Typography sx={{ py: 4, textAlign: "center", color: BRAND.muted }}>No hay proyectos para mostrar.</Typography></Grid> : null}
              </Grid>
            </Stack>
          ) : null}
        </Box>
      </Paper>

      <ProjectPricingDialog
        open={Boolean(pricingProject)}
        project={pricingProject}
        services={data?.services || []}
        saving={saving}
        onClose={() => setPricingProject(null)}
        onSave={updateProjectPricing}
      />

      <AdminSettlementDialog
        open={Boolean(settlement)}
        project={settlement?.project || null}
        action={settlement?.action || "manual_payment"}
        saving={saving}
        onClose={() => setSettlement(null)}
        onConfirm={settleProject}
      />

      <ProjectModulesDialog
        open={Boolean(modulesProject)}
        project={modulesProject}
        catalog={data?.moduleCatalog || []}
        saving={saving}
        onClose={() => setModulesProject(null)}
        onSave={updateProjectModules}
      />
    </Stack>
  );
}
